import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import WhatIfResultsDetailPres from '../presentational/WhatIfResultsDetailPres';
import { setPageType, getCalculations } from '../state/whatIfActions';
import { WHAT_IF_RESULTS_URL } from 'src/constants/constants.js';
import intl from 'react-intl-universal';
import { withSnackbar } from 'notistack';
import Spinner from 'src/ui/library/Spinner/Spinner';

class WhatIfResultsDetailContainer extends React.Component {
  static propTypes = {
    enqueueSnackbar: PropTypes.func.isRequired,
  };

  state = {
    totalSalaryBarData: [],
    radialData: [],
    otherConsiderationsData: {},
    selectedConsiderationData: '',
    keys: ['homeCountry', 'hostCountry', 'level', 'maritalStatus', 'noOfEmployees', 'startDate', 'endDate'],
    currencySet: ['USD'],
    outputCurrency: { value: 'USD', label: 'USD' },
    openDialog: false,
    params: [],
    showSelectionButtons: false,
  };

  componentDidMount() {
    this.getResultsData();
  }

  onExit = () => {
    this.props.setPageType('list');
    this.props.push(WHAT_IF_RESULTS_URL);
  };

  formatCurrency = (value, outputCurrency) => {
    var formatter = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
    });

    var prefix = intl.get('whatif.calculator.currency.prefix')[outputCurrency];
    var suffix = intl.get('whatif.calculator.currency.suffix')[outputCurrency];

    return (prefix ? prefix : '') + formatter.format(value) + (suffix ? suffix : '');
  };

  getResultsData = () => {
    const { resultToShow, colors, detailIndex, employees } = this.props;

    let totalSalaryBarData = [
      { value: resultToShow.baseCompensation.grossBaseSalary, name: 'Base Salary' },
      { value: resultToShow.baseCompensation.supplementalBase, name: 'Supplemental' },
      { value: resultToShow.baseCompensation.bonus1, name: 'Bonus 1' },
      { value: resultToShow.baseCompensation.bonus2, name: 'Bonus 2' },
      { value: resultToShow.baseCompensation.pensionContributionEmployer, name: 'Pension C. - Employer' },
      { value: resultToShow.baseCompensation.pensionContributionEmployee, name: 'Pension C. - Employee' },
      { value: resultToShow.baseCompensation.otherBaseTaxable1, name: 'Other - Taxable 1' },
      { value: resultToShow.baseCompensation.otherBaseTaxable2, name: 'Other - Taxable 2' },
    ];

    let radialData = [
      { value: resultToShow.baseCompensation.total, name: 'Base Compensation', color: colors[0] },
      { value: resultToShow.baseCompensation.bonus1 + resultToShow.baseCompensation.bonus2, name: 'Bonuses', color: colors[4] },
      { value: resultToShow.relocationCosts.total, name: 'Relocation Costs', color: colors[1] },
      { value: resultToShow.assignmentAllowances.total, name: 'Assignment Allowances', color: colors[2] },
      {
        value: resultToShow.taxes.homeTaxes.total + resultToShow.taxes.hostTaxes.total + resultToShow.taxes.hypoTaxes.total,
        name: 'Taxes',
        color: colors[3],
      },
    ];

    let otherConsiderationsData = {
      'Immigration information': resultToShow.immigrationNotes,
      'Host country assumptions': resultToShow.hostAssumptions,
      'Home country assumptions': resultToShow.homeAssumptions,
    };

    const selectedEmpKey = Object.keys(employees).filter((key, index) => index === detailIndex)[0];

    this.setState({
      totalSalaryBarData,
      radialData,
      otherConsiderationsData,
      selectedConsiderationData: Object.keys(otherConsiderationsData)[0],
      selectedEmpKey,
    });
  };

  handleOnClick = selectedConsiderationData => {
    this.setState({ selectedConsiderationData });
  };

  onCalculate = () => {
    this.calculate(this.props.employees, this.props.outputCurrency);
  };

  isEmployeeValid = (employee, index) => {
    const { enqueueSnackbar, countriesDictionary } = this.props;

    let isValid = true;
    let errorMsg = ['Employee Type ' + index];
    let fields = [];
    const isEmpty = Object.values(employee).every(x => x === null || x === '' || x === undefined);

    if (!isEmpty) {
      if (employee.homeCountry && employee.hostCountry) {
        const homeCountry = countriesDictionary[employee.homeCountry];
        if (!homeCountry) {
          errorMsg.push(intl.get('whatif.calculator.messages.notifyUnsupportedCountries'));
          isValid = false;
        } else {
          const hostCountry = countriesDictionary[employee.homeCountry].countries[employee.hostCountry];
          if (!hostCountry) {
            errorMsg.push(intl.get('whatif.calculator.messages.notifyUnsupportedCountries'));
            isValid = false;
          }
        }
      } else {
        // errorMsg.push(intl.get('whatif.calculator.messages.countriesRequired'));
        fields.push('Home/Host country');
        isValid = false;
      }
      if (employee.level === undefined || this.props.lookups.levelsList.indexOf(employee.level) < 0) {
        // errorMsg.push(intl.get('whatif.calculator.messages.levelRequired'));
        fields.push('Level');
        isValid = false;
      }
      if (employee.maritalStatus === undefined || this.props.lookups.maritalStatusList.indexOf(employee.maritalStatus) < 0) {
        // errorMsg.push(intl.get('whatif.calculator.messages.maritalStatusRequired'));
        fields.push('Marital Status');
        isValid = false;
      }
      if (employee.noOfEmployees === undefined || employee.noOfEmployees < 1) {
        // errorMsg.push(intl.get('whatif.calculator.messages.numEmployeeRequired'));
        fields.push('Number of Employees');
        isValid = false;
      }
      if (
        employee.endDate === undefined ||
        employee.startDate === undefined ||
        !moment(employee.startDate, 'D-MMM-YYYY').isValid() ||
        !moment(employee.endDate, 'D-MMM-YYYY').isValid()
      ) {
        // errorMsg.push(intl.get('whatif.calculator.messages.datesRequired'));
        fields.push('Start/End date');
        isValid = false;
      } else if (moment(employee.startDate, 'D-MMM-YYYY') >= moment(employee.endDate, 'D-MMM-YYYY')) {
        errorMsg.push(intl.get('whatif.calculator.messages.startBeforeEndDate'));
        isValid = false;
      }
    } else {
      errorMsg.push(intl.get('whatif.calculator.messages.emptyRow'));
      isValid = false;
    }

    if (!isValid) {
      if (fields.length > 0) {
        errorMsg.push('Required Fields: ' + fields.join(', '));
      }
      enqueueSnackbar(<div style={{ 'white-space': 'pre-line' }}>{errorMsg.join('\n')}</div>, { variant: 'error' });
    }

    return isValid;
  };

  calculate = (employees, outputCurrency) => {
    const { countriesDictionary } = this.props;
    let error = false;
    const params = [];

    let index = 1;
    Object.keys(employees).forEach(i => {
      const employee = employees[i];
      const isValid = this.isEmployeeValid(employee, index++);
      const sheetName = employee.homeCountry + 'To' + employee.hostCountry;
      if (isValid) {
        params.push({
          sheet: sheetName,
          hostCountry: countriesDictionary[employee.homeCountry].countries[employee.hostCountry].id,
          homeCountry: countriesDictionary[employee.homeCountry].id,
          classification: employee.level,
          policy: employee.policy,
          maritalStatus: employee.maritalStatus,
          familySize: Number(employee.familySize),
          numChildren: employee.numOfSchoolAgedChildren ? Number(employee.numOfSchoolAgedChildren) : 0,
          numEmployees: Number(employee.noOfEmployees),
          periodToBeCalculatedStart: moment(employee.startDate, 'DD-MMM-YYYY')
            .utc()
            .format(),
          periodToBeCalculatedEnd: moment(employee.endDate, 'DD-MMM-YYYY')
            .utc()
            .format(),
          outputCurrency: outputCurrency.value,
          salary: Number(employee.salary),
        });
      } else {
        error = true;
      }
    });

    if (error) {
      return;
    }

    this.props.getCalculations(params, true);
  };

  render() {
    if (this.props.isLoading || !this.props.countriesDictionary) {
      return (
        <div style={{ marginTop: '10%' }}>
          <Spinner size={130} style={{ marginTop: '50px' }} />
        </div>
      );
    }

    return (
      <WhatIfResultsDetailPres
        {...this.props}
        {...this.state}
        onExit={this.onExit}
        formatCurrency={this.formatCurrency}
        handleOnClick={this.handleOnClick}
        onCalculate={this.onCalculate}
      />
    );
  }
}

const mapStateToProps = state => ({
  colors: state.whatIf.colors,
  detailIndex: state.whatIf.detailIndex,
  employees: state.whatIf.employees,
  lookups: state.whatIf.lookups,
  isLoading: state.whatIf.isLoadingCalculations,
  countriesDictionary: state.whatIf.countriesDictionary,
  outputCurrency: state.whatIf.outputCurrency,
});

const mapDispatchToProps = {
  setPageType,
  getCalculations,
};

export default connect(mapStateToProps, mapDispatchToProps)(withSnackbar(WhatIfResultsDetailContainer));
